import { PathPickSmartStoreInfoResponse } from "@/features/pathpick/features/basket/types/response";
import { PathPickNaverPartnerAddressBook } from "@/features/pathpick/features/basket/types";
import { PathPickMarketSyncKind } from "@/features/pathpick/types";
import {
  isApiSyncInfoComplete,
  isFileRequestInfoComplete,
} from "@/features/pathpick/features/basket/utils";
import {
  PathPickSyncFlowStep,
  PathPickSyncIntent,
} from "@/features/pathpick/features/basket/contexts/syncFlowReducer";

// 관문 판정에 필요한 상태. 훅이 서버 상태와 선택 상태를 모아 넘기고,
// 저장/재조회 직후에는 캐시 반영을 기다리지 않도록 응답 값으로 일부를 덮어쓴다.
export interface PathPickSyncGateConditions {
  // 이 의도로 실제 접수될 상품 수 — 이미 완료됐거나 처리 중인 상품은 빠진 값이다.
  // 의도(어떤 채널로 신청하는지)에 따라 달라지므로 호출부가 의도별로 계산해 넘긴다.
  requestableCount: number;
  smartStoreInfo: PathPickSmartStoreInfoResponse | null | undefined;
  isNaverPartnerConnected: boolean;
  // 해외 출고지 판정 소스 — 네이버 주소록 조회 결과
  addressBooks: PathPickNaverPartnerAddressBook[] | undefined;
  // API 연동으로 신청할 수 있는 마켓(네이버 + 권한 받은 마켓)
  syncKinds: PathPickMarketSyncKind[];
}

// 네이버 스마트스토어로 API 연동할 준비가 됐는지 — 제휴 연결 + 판매자 기본 정보(A/S·배송·해외 출고지).
export const isNaverSyncReady = ({
  smartStoreInfo,
  isNaverPartnerConnected,
  addressBooks,
}: Pick<
  PathPickSyncGateConditions,
  "smartStoreInfo" | "isNaverPartnerConnected" | "addressBooks"
>) =>
  isNaverPartnerConnected && isApiSyncInfoComplete(smartStoreInfo, addressBooks);

// 이 API 연동 신청에 네이버 준비가 필요한가.
// 마켓을 골랐으면 네이버를 골랐을 때만, 고르기 전이면 고를 마켓이 네이버뿐일 때만 필요하다 —
// 네이버 외 마켓 권한이 있는 바이어가 네이버 설정 없이도 다른 마켓으로 연동할 수 있어야 한다(2026-10-16).
export const needsNaverSync = (
  markets: PathPickMarketSyncKind[] | undefined,
  syncKinds: PathPickMarketSyncKind[]
) =>
  markets
    ? markets.includes("NAVER_SYNC")
    : syncKinds.length === 1 && syncKinds[0] === "NAVER_SYNC";

// 관문을 순서대로 검사해 "멈춰야 할 단계"를 돌려준다. null이면 전부 통과해 신청을 실행할 수 있다.
// 서버 상태를 인자로만 받는 순수 함수라 관문 순서를 단독으로 검증할 수 있다.
export const resolveBlockingStep = (
  intent: PathPickSyncIntent,
  conditions: PathPickSyncGateConditions
): PathPickSyncFlowStep | null => {
  const { requestableCount, smartStoreInfo, syncKinds } = conditions;

  // 접수할 대상이 하나도 없으면(선택 전체가 이미 완료됐거나 처리 중) 어떤 의도든 여기서 끝난다 —
  // 사용자가 선택을 바꿔야 해서 이 자리에서 해결할 수 없는 재개 불가 단계다.
  // 일부만 처리된 경우는 막지 않고 나머지만 접수한다(호출부가 body에서 제외한다).
  if (requestableCount === 0) {
    return { name: "alreadyRequested" };
  }

  // 파일 신청 — 기본 정보는 스마트스토어 양식에만 필요하므로 KREAM 단독 신청은 관문 없이 통과한다.
  if (intent.kind === "fileRequest") {
    return intent.formats.includes("NAVER_EXCEL") &&
      !isFileRequestInfoComplete(smartStoreInfo)
      ? { name: "buyerInfo", variant: "fileRequest" }
      : null;
  }
  // 네이버 API 연동 필수값(스마트스토어 API 정보 · A/S 정보 · 해외 출고지)은 기본 정보 설정 모달 한 곳에서
  // 모두 해결한다. 스마트스토어 API 설정 여부는 제휴 연결 상태(connected)로 판정한다.
  // 네이버를 고르지 않은 신청(머스트잇·트렌비·카페24만)은 이 관문을 거치지 않는다 — 그 마켓의 연결·권한은 서버가 본다.
  if (
    needsNaverSync(intent.markets, syncKinds) &&
    !isNaverSyncReady(conditions)
  ) {
    return { name: "buyerInfo", variant: "apiSync" };
  }
  return null;
};
